import {
  Box,
  Button,
  FlexContainer,
  IconLibrary,
  ReemaThemeV2,
} from '@act/storybook';
import { formatPhoneNumber } from '@act/shared/utilities';
import { CallDto } from '@act/shared/data-transfer-objects';
import { useMobile } from '@act/utilities/MobileContext';
import { Box as MuiBox, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigateToMemberPage } from 'react-components/app/routing/navigationHooks.util';
import { useInteractionsDialog } from 'react-components/dialog-forms/hooks/useInteractionsDialog.hook';
import { ActiveCallMenu } from 'react-components/member-page/Calls/ActiveCallMenu';
import { actingUserAppSelectors } from 'redux/redux';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';
import { utilityAppSelectors } from 'redux/utils/selectors/utilitySelectors';
import { displayName } from 'utilities/formatters/DisplayName';
import { JustInTimeCallDragHandle } from './JustInTimeCallDragHandle';
import { useJustInTimeCallCardPosition } from './hooks/useJustInTimeCallCardPosition.hook';

const JIT_CARD_ELEVATION_SHADOW =
  '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)';

export interface JustInTimeCallCardProps {
  call: CallDto;
  stackIndex: number;
}

/**
 * Draggable JIT notification card for an active call.
 *
 * @param props - Call payload and stack index for default positioning.
 * @returns Fixed-position call card or null when hidden by flags/state.
 */
export const JustInTimeCallCard = ({
  call,
  stackIndex,
}: JustInTimeCallCardProps) => {
  const { isMobile } = useMobile();
  const palette = ReemaThemeV2.LIGHT_MODE.palette;
  const threeWayCallsFF = useFeatureFlag('three-way-calls');
  const threeWayKnownCallerFF = useFeatureFlag('three-way-known-caller');
  const interactionsDialog = useInteractionsDialog();
  const navigateToMemberPage = useNavigateToMemberPage();
  const currentUser = actingUserAppSelectors.useSelectActingUser();
  const caller = utilityAppSelectors.selectUserOrMemberById(
    call?.originator?.userId
  );
  const receiver = utilityAppSelectors.selectUserOrMemberById(
    call?.receiver?.userId
  );
  const subjectMember = utilityAppSelectors.selectUserOrMemberById(
    call?.subjectId || null
  );

  const [openThreeWayCallsMenu, setOpenThreeWayCallsMenu] = useState(false);

  const callSubject = call.incoming
    ? caller || subjectMember
    : receiver || subjectMember;

  const phoneNumber =
    call.originator?.phoneNumber || call.receiver?.phoneNumber;

  const currentUserCallConnection = call.connections.find(
    connection =>
      connection.userId === currentUser?.id && !connection.completedAt
  );
  const inviteLog = currentUserCallConnection?.connectionLogs.find(
    log => log.event === 'invite'
  );
  const inviteGuide = inviteLog?.actingUser
    ? displayName(inviteLog.actingUser)
    : null;

  const callDisplayName = callSubject ? displayName(callSubject) : 'Unknown';

  const hasActiveConnection = call.connections?.some(
    conn => conn.userId === currentUser?.id && !conn.completedAt
  );

  const shouldShow =
    threeWayCallsFF &&
    (threeWayKnownCallerFF || call.wasOriginallyUnknown) &&
    hasActiveConnection;

  const { cardRootRef, dragHandleProps, containerPositionStyle, isPositioned, maxHeightPx } =
    useJustInTimeCallCardPosition({
      stackIndex,
      isMobile,
      enabled: shouldShow,
    });

  const handleOpenCall = (e: MouseEvent) => {
    e.stopPropagation();

    const isAssignedToCurrentGuide =
      callSubject?.assignedGuideId === currentUser?.id;

    if (isAssignedToCurrentGuide && callSubject) {
      interactionsDialog.editCall({ call, user: callSubject });
      navigateToMemberPage(currentUser.id, callSubject.id);
    } else {
      setOpenThreeWayCallsMenu(!openThreeWayCallsMenu);
    }
  };

  if (!shouldShow) {
    return null;
  }

  const expandedCardMaxWidth = isMobile ? '90vw' : '640px';
  // Width belongs on the scroll shell only. Matching fixed width on the inner
  // card would exceed the shell's client width once a vertical scrollbar appears,
  // which forces an unwanted horizontal scrollbar.
  const collapsedCardMaxWidth = isMobile ? '90vw' : '500px';

  return (
    <MuiBox
      ref={cardRootRef}
      style={{
        position: 'fixed',
        left: containerPositionStyle.left,
        top: containerPositionStyle.top,
        zIndex: containerPositionStyle.zIndex,
        opacity: isPositioned ? 1 : 0,
        pointerEvents: isPositioned ? 'auto' : 'none',
        maxHeight: `${maxHeightPx}px`,
        width: openThreeWayCallsMenu ? expandedCardMaxWidth : undefined,
        maxWidth: openThreeWayCallsMenu ? expandedCardMaxWidth : undefined,
        overflowX: openThreeWayCallsMenu ? 'hidden' : 'visible',
        overflowY: openThreeWayCallsMenu ? 'auto' : 'visible',
        boxSizing: 'border-box',
      }}
    >
      <Box
        backgroundColor={palette.common.white}
        border={`1px solid ${palette.strokePrimary}`}
        borderRadius="8px"
        boxShadow={JIT_CARD_ELEVATION_SHADOW}
        padding="12px 16px 12px 8px"
        minWidth={
          openThreeWayCallsMenu ? undefined : isMobile ? '320px' : '400px'
        }
        maxWidth={
          openThreeWayCallsMenu ? '100%' : collapsedCardMaxWidth
        }
        width={openThreeWayCallsMenu ? '100%' : undefined}
        boxSizing="border-box"
      >
        <FlexContainer
          direction="row"
          alignItems="flex-start"
          height="auto"
          overflow="hidden"
        >
          <JustInTimeCallDragHandle {...dragHandleProps} />
          <Box flex={1} minWidth="0" overflow="hidden">
            <FlexContainer
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              height="auto"
              flexWrap
            >
              <FlexContainer
                direction="row"
                alignItems="center"
                gap={1.5}
                flexGrow={1}
                minWidth="0"
                height="auto"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="40px"
                  height="40px"
                  borderRadius="50%"
                  backgroundColor="#E8F5F3"
                  flexShrink={0}
                >
                  <IconLibrary
                    iconName="Phone"
                    size={20}
                    weight="regular"
                    color="#00796B"
                  />
                </Box>
                <Box flex={1} minWidth="0" overflow="hidden">
                  {inviteLog && inviteGuide ? (
                    <Typography
                      variant="bodySmall"
                      color="text.secondary"
                      noWrap
                    >
                      Invite to join from {inviteGuide}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="titleMedium"
                    color="text.primary"
                    fontWeight={500}
                    noWrap
                  >
                    {callDisplayName}
                  </Typography>
                  {phoneNumber ? (
                    <Typography
                      variant="bodyMedium"
                      color="text.secondary"
                      noWrap
                    >
                      {formatPhoneNumber(phoneNumber)}
                    </Typography>
                  ) : null}
                </Box>
              </FlexContainer>
              <Button
                variant="contained"
                onClick={handleOpenCall}
                className="jit-call-open-button"
                startIcon={
                  <IconLibrary iconName="Phone" weight="regular" size={18} />
                }
              >
                {openThreeWayCallsMenu ? 'Close call' : 'Open call'}
              </Button>
            </FlexContainer>
            {openThreeWayCallsMenu ? (
              <Box marginTop="16px" width="100%" minWidth="0">
                <ActiveCallMenu call={call} />
              </Box>
            ) : null}
          </Box>
        </FlexContainer>
      </Box>
    </MuiBox>
  );
};
